import {
  ContainerSizes,
  PageContainer,
  Spinfinity,
  Typography,
} from 'components';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useEffect, useMemo } from 'react';
import usePublishedGuides from 'services/general/guidesList';
import { GuideCard } from './GuideCard/GuideCard';
import { computeAvailablePills, matchesGuideFilters } from './Guides.helpers';
import styles from './Guides.module.css';
import { ResourcesEmptyState } from './ResourcesEmptyState/ResourcesEmptyState';
import { VideoResources } from './VideoResources/VideoResources';
import {
  matchesFilters,
  ResourceFilters,
} from './VideoResources/VideoResources.helpers';
import { useContentFilters } from './hooks/useContentFilters';
import { useVisibleVideoResources } from './hooks/useVisibleVideoResources';

export const Guides = () => {
  const { data: guides, isError } = usePublishedGuides();
  const { trackEvent } = useAnalytics();
  const { visibleResources, hideTinyPlusContent } = useVisibleVideoResources();

  useEffect(() => {
    trackEvent('Resources Viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableTags = useMemo(
    () => computeAvailablePills(visibleResources, guides),
    [visibleResources, guides],
  );

  // Total across both sections — the filter bar now drives webinars and guides.
  const countMatches = (filters: ResourceFilters) =>
    (visibleResources?.filter(v => matchesFilters(v, filters)).length ?? 0) +
    (guides?.filter(g => matchesGuideFilters(g, filters)).length ?? 0);

  const { filters, toggleTag, toggleTinyPlusOnly } = useContentFilters(
    countMatches,
    { allowTinyPlusOnly: !hideTinyPlusContent },
  );

  const filteredGuides = useMemo(
    () => guides?.filter(guide => matchesGuideFilters(guide, filters)),
    [guides, filters],
  );

  const isFiltering = filters.tags.length > 0 || filters.tinyPlusOnly;

  return (
    <PageContainer size={ContainerSizes.LG} className={styles.pageContainer}>
      <VideoResources
        filters={filters}
        availableTags={availableTags}
        showTinyPlusFilter={!hideTinyPlusContent}
        onToggleTag={toggleTag}
        onToggleTinyPlusOnly={toggleTinyPlusOnly}
      />
      <Typography variant='heading-l'>Blogs & guides</Typography>
      <div className='mb-3' />
      {!filteredGuides ? (
        isError ? (
          <p>{`We can't display the guides at the moment`}</p>
        ) : (
          <Spinfinity />
        )
      ) : filteredGuides.length ? (
        <div className={styles.guidesWrapper}>
          {filteredGuides.map(guide => (
            <GuideCard key={guide.id} guide={guide} />
          ))}
        </div>
      ) : (
        isFiltering && <ResourcesEmptyState />
      )}
    </PageContainer>
  );
};
